import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

//
import { ExtraHeaderContent, Header } from 'adminDashboard/benefitDetails/common/ProductClassSummaryComponents'

import { getBenefitDetails } from 'core'
//
import getProductClassDesignSummary, {
  ProductClassDesignSummaryResponse,
} from 'core/application/adminDashboard/benefitDetails/design/perks/productClass/getProductClassDesignSummary'
import { frequencies } from 'core/domain/benefit/allowanceFrequency'

import { Button, Card, Col, Collapse, CollapseItem, Icon, Row, Skeleton } from 'common/components'
import { formatYear, parseZoneFormat } from 'common/dateFormatters'
import { useQueryRequestV2 } from 'common/hooks'

import { SubCollapseElement } from '../subCollepse/SubCollapseElement'
import { ProductClassProps } from './perksProductClassDetails'

import './product-class-details.less'

const ProductClassDetailsOnBenefitDesign = ({
  item,
  collapseKey,
  colSpan = 12,
  planPeriodId,
  onEdit,
  update = false,
  setUpdate = () => undefined,
  onDelete,
}: ProductClassProps) => {
  const { t: translate } = useTranslation()
  const [rowsToDisable, setRowsToDisable] = useState([])

  const { data: activePlanPeriod } = useQueryRequestV2({
    queryFn: () => {
      return getBenefitDetails(planPeriodId)
    },
  })

  const {
    data: productClasses,
    isLoading: areProductClassesLoading,
    reload: reloadProductClassDetails,
  } = useQueryRequestV2<ProductClassDesignSummaryResponse>({
    queryFn: () => getProductClassDesignSummary(item.id),
    initialState: { eligibleExpenseCategories: [], waitingPeriod: 0, totalAllowance: ' ', offerSameAllowance: false },
  })

  /**
   * update is a flag sent by parent to trigger details update from back-end,
   * the component after update finished sets the flag again to false
   */
  useEffect(() => {
    if (update) {
      reloadProductClassDetails().finally(() => {
        setUpdate(false)
      })
    }
  }, [update])

  useEffect(() => {
    let productClassCategories = productClasses.eligibleExpenseCategories.map((el) => {
      return { name: el.expenseCategoryId, isDisable: false }
    })
    setRowsToDisable(productClassCategories)
  }, [productClasses])

  return item.designCompleted ? (
    <Row className={'mb-8'}>
      <Col span={colSpan} className="pk-perks__design-classes">
        <Row>
          <Col span={24}>
            <Row>
              <Col span={18}>
                <Collapse className="design_classes_collapse w-100">
                  <CollapseItem
                    className="class_design"
                    key={collapseKey}
                    header={<Header title={item.name} />}
                    extra={
                      <ExtraHeaderContent
                        content={
                          <div>
                            {/*{onEdit && item.totalEmployees === 0 && (*/}
                            {onEdit && (
                              <Button className="mr-16" onClick={onEdit}>
                                {translate('admin-dashboard.benefit-details.summary.edit-button')}
                              </Button>
                            )}

                            {onDelete && item.totalEmployees === 0 && (
                              <Icon className="fal fa-trash-alt" onClick={onDelete} />
                            )}
                          </div>
                        }
                      />
                    }
                    content={
                      <Skeleton loading={areProductClassesLoading}>
                        {/*{(item.totalEmployees === 0 || !activePlanPeriod?.hasStarted) && (*/}
                        <div>
                          {productClasses.eligibleExpenseCategories.map((item, index) => (
                            <SubCollapseElement
                              key={item.expenseCategoryId}
                              collapseKey={index}
                              title={item.expenseCategoryName}
                              monthlyAllowance={item.allowance}
                              offerSameAllowance={productClasses.offerSameAllowance}
                              allowanceFrequency={frequencies[item.allowanceFrequency]?.name}
                              eligibleCategories={item.eligibleExpenseTypes}
                              className={`${productClasses.eligibleExpenseCategories.length !== index + 1 && 'mb-16'}`}
                              startDate={item.startDate}
                              endDate={item.endDate}
                              activePlanPeriodEndDate={activePlanPeriod?.endDate}
                            />
                          ))}
                          {item &&
                            productClasses &&
                            item.offerSameAllowance &&
                            productClasses.totalAllowance &&
                            productClasses.eligibleExpenseCategories &&
                            activePlanPeriod && (
                              // item.totalEmployees !== 0 &&(
                              <Row className={'card_details'}>
                                <Card>
                                  <Row>
                                    <Col span={18}>
                                      <h5>{translate('admin-dashboard.benefit-details.summary.total-allowance')} </h5>
                                    </Col>
                                    <Col flex={'auto'}>
                                      {' '}
                                      <Icon className="fal fa-money-bill mr-8" />$ {productClasses.totalAllowance}
                                    </Col>
                                  </Row>
                                  <Row>
                                    <Col span={18}>
                                      <h5> {translate('admin-dashboard.benefit-details.summary.perk-duration')}n </h5>
                                    </Col>
                                    <Col flex={'auto'}>
                                      <Icon className="fal fa-clock mr-8" />
                                      {parseZoneFormat(productClasses.eligibleExpenseCategories[0]?.startDate)} -{' '}
                                      {formatYear(productClasses.eligibleExpenseCategories[0]?.endDate) !==
                                      formatYear(activePlanPeriod.endDate)
                                        ? parseZoneFormat(productClasses.eligibleExpenseCategories[0]?.endDate)
                                        : 'Continuous'}
                                    </Col>
                                  </Row>

                                  <Row>
                                    <Col span={18}>
                                      <h5>
                                        {translate('admin-dashboard.benefit-details.summary.waiting-period-label')}
                                      </h5>
                                    </Col>
                                    <Col flex={'auto'}>
                                      <Icon className="fal fa-hourglass mr-8" />
                                      {productClasses.waitingPeriod !== 0
                                        ? `${productClasses.waitingPeriod} ${translate(
                                            'admin-dashboard.benefit-details.summary.waiting-period-value-label',
                                          )}`
                                        : `${translate(
                                            'admin-dashboard.benefit-details.summary.no-waiting-period-value-label',
                                          )}`}
                                    </Col>
                                  </Row>

                                  <Row>
                                    <Col span={18}>
                                      <h5>
                                        {translate('admin-dashboard.benefit-details.summary.allowance-frequency-label')}
                                      </h5>
                                    </Col>
                                    <Col flex={'auto'}>
                                      <Icon className="fal fa-repeat mr-8" />
                                      {
                                        frequencies[productClasses.eligibleExpenseCategories[0]?.allowanceFrequency]
                                          ?.name
                                      }
                                    </Col>
                                  </Row>
                                </Card>
                              </Row>
                            )}
                        </div>
                        {/*)}*/}
                      </Skeleton>
                    }
                  />
                </Collapse>
              </Col>
            </Row>
            {/*</Card>*/}
          </Col>
        </Row>
      </Col>
    </Row>
  ) : (
    <Row className={'mb-8'}>
      <Col span={12}>
        <Card key={item.id}>
          <Row>
            <Col span={22}>
              <h5>{item.name}</h5>
            </Col>
            <Col span={2} className={'text-right'}>
              <Icon className="fal fa-trash-alt" onClick={onDelete} />
            </Col>
          </Row>
          <Button type={'primary'} ghost onClick={onEdit}>
            {translate('admin-dashboard.benefit-details.perks-benefit-design.button-title')}
          </Button>
        </Card>
      </Col>
    </Row>
  )
}

export default ProductClassDetailsOnBenefitDesign
